import * as React from "react";
import {
    Link,
    makeStyles,
    Text,
    Image
} from "@fluentui/react-components";
import { CodeBlock } from "../../components/CodeBlock";
import { CardSection } from "../../components/CardSection";
import { centered } from "../../styles/GlobalStyles";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px"
    },
    centered
});

export const Id = "6c6c236a-86da-44c6-8c5b-7ed34946fc35";
export const Title = "How does Ianus Guard work?";
export const Subtitle = "Architecture and flow of Ianus license validation";
export const Description = "Understand the cryptographic principles and validation process behind Ianus";
export const Keywords = ["encryption", "security", "flow", "architecture"];

const licenseTokenExample = `{
    "jti": "5ace49ad-3a47-47ab-a64f-76e57e7bf44e",
    "iss": "https://www.dev.ianusguard.com/api/public/products/7216ad86-0400-487c-9072-5017e7ff2ad1",
    "aud": "ianusguard",
    "pub": "bbb856fc-a799-441b-bb77-6e4480c70071",
    "prd": "7216ad86-0400-487c-9072-5017e7ff2ad1",
    "sub": "e01a0efa-50df-44cd-a77a-353bce5db849",
    "env": [
        {
            "type": "dataverse",
            "identifier": "deadbeef-babe-44fc-a8a3-4ce890fd955c",
            "name": "Demo Org"
        }
    ],
    "iat": 1754119732,
    "nbf": 1754119732,
    "exp": 1756798132,
    "iss_meta": {
        "name": "Demo Product by Ianua Software"
    },
    "aud_meta": {
        "name": "Ianus Guard"
    },
    "pub_meta": {
        "name": "Ianua Software"
    },
    "prd_meta": {
        "name": "Demo Product"
    },
    "sub_meta": {
        "name": "Demo Customer"
    },
    "ver": "1.0"
}`;

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();

    return (
        <div className={styles.container}>
            <CardSection title="Overview">
                <Text>
                    Ianus Guard is a cryptographic licensing system. It allows publishers to their products, protect them using an open source tookit and ship them to customers, where they are validated by digitally signed licenses - without requiring external APIs at runtime.
                </Text>
            </CardSection>

            <CardSection title="Components">
                <Text>
                    The diagram below illustrates how the Ianus Guard platform and clients work together.
                    You manage your publisher, products and customers inside Ianus Guard platform and can issue licenses for them.
                    The Ianus Guard clients are embedded in your products, which are shipped to your customer and validated by licenses in their surrounding environment.
                    <br />
                    <br />
                    <Image
                        className={styles.centered}
                        alt="Ianus Guard topology"
                        width="75%"
                        src="/assets/knowledgearticle/ianus-topology.svg"
                    />

                </Text>
            </CardSection>

            <CardSection title="Key principles">
                <ul>
                    <li><b>Cryptographic security:</b> Each license is signed with the publisher’s private key.</li>
                    <li><b>Offline validation:</b> Licenses can be verified using the public key embedded in the app/plugin.</li>
                    <li><b>Tamper-proof:</b> Licenses are encoded as JWTs and cannot be forged or modified.</li>
                </ul>
            </CardSection>

            <CardSection title="What is a license?">
                <Text>
                    Licenses are JSON Web Tokens (JWTs) issued by Ianus and signed with a private key.
                    The license key is not human readable, it is an encoded string that contains digitally signed claims.
                    These claims are what defines the license such as the customer, expiration date, and the product they're issued for.
                    They can be parsed and shown in a human readable way, such as below:
                </Text>
                <br />
                <br />
                <CodeBlock code={licenseTokenExample} language="json" />
                <br />
                <br />
                <Text>
                    The <code>prd</code> claim maps to your external Product ID, the <code>pub</code> to your external Publisher ID and <code>sub</code> to the external customer ID. Licenses are time-limited (an <code>exp</code> claim is set) or unlimited (no <code>exp</code> claim).
                </Text>
            </CardSection>

            <CardSection title="How does validation work?">
                <Text>
                    Validation runs directly inside your PCF or plugin. Here's the typical flow:
                </Text>
                <ol>
                    <li>Your app retrieves the license via dataverse (Dataset, Web API, RetrieveMultiple).</li>
                    <li>License claims are verified against the information you ship and the current environment.</li>
                    <li>The token is verified against the public key you ship.</li>
                    <li>If valid, claims are extracted and exposed in a result object.</li>
                    <li>If invalid, rendering or execution is prevented.</li>
                </ol>
                <Text>
                    No external API check is required. Tokens are self-contained and use asymmetric RSA signatures.
                </Text>
            </CardSection>

            <CardSection title="Understanding the Public Key - Verification, Not Encryption">
                <Text>
                    The public key verifies the authenticity of licenses. Each license contains a header, its content and a signature signed with your private key.
                </Text>
                <ul>
                    <li>Altered license header or content → signature mismatch → rejected.</li>
                    <li>Fake license creation is impossible without your private key.</li>
                    <li>Public keys ensure tamper-proof validation.</li>
                </ul>
            </CardSection>

            <CardSection title="Where is the license stored?">
                <Text>
                    We ship a ian_license entity that is used for storing licenses.
                    <br />
                    <br />
                    This entity is gathered and validated by various clients:
                    <ul>
                        <li><strong>Canvas Apps</strong>: Use Datasets for organization and ian_license and local validation</li>
                        <li><strong>PCFs</strong>: Use webAPI and local validation</li>
                        <li><strong>Plugins</strong>: Use RetrieveMultipleRequests and local validation (plugin packages), or ian_LicenseValidation custom API (plugin assemblies)</li>
                        <li><strong>Flows</strong>: Use ian_LicenseValidation custom API</li>
                        <li><strong>External tools and other use cases</strong>: Use ian_LicenseValidation custom API</li>
                    </ul>
                </Text>
            </CardSection>

            <CardSection title="What about license updates?">
                <Text>
                    Customers can paste in updated license tokens at any time. The new license will be considered for future validations.
                    If a token expires or is tampered with, the validation fails and execution is blocked.
                </Text>
            </CardSection>

            <CardSection title="Learn more">
                <Text>
                    For a technical deep dive, see our{" "}
                    <Link href="https://github.com/Ianua-Software/Ianus-Clients" target="_blank">
                        Ianus Clients GitHub repository
                    </Link>{" "}
                    or read about{" "}
                    <Link href="https://datatracker.ietf.org/doc/html/rfc7519" target="_blank">
                        JSON Web Tokens (RFC 7519)
                    </Link>.
                </Text>
            </CardSection>
        </div>
    );
});
